import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

export interface RuntimeFeatureFlags {
  workbench: {
    homeDashboard: boolean
    reducedMode: boolean
    reviewTab: boolean
    experimentalTabs: boolean
    sourceStructure: boolean
  }
  ledgerSafe: {
    enabled: boolean
    piDock: boolean
    logTab: boolean
    propagation: boolean
    editSuggestions: boolean
    experimentalMaps: boolean
    newWitnessButton: boolean
    oracleExport: boolean
    sceneTitles: boolean
    rowTitleGeneration: boolean
    rowTitleSidecarImport: boolean
    structuralRanges: boolean
    structuralRangeGeneration: boolean
    snapshots: boolean
    traditionReader: boolean
    characterIndex: boolean
    cameraLucida: boolean
    postgresTrial: boolean
    postgresFullMirror: boolean
    postgresPrimary: boolean
    postgresPrimaryBootstrap: boolean
    postgresPrimaryCutover: boolean
    postgresPrimaryFocusedCache: boolean
  }
  build: {
    adaptiveLargeExecutor: boolean
    savedRunImport: boolean
  }
  sharing: {
    linkSharing: boolean
    r2Storage: boolean
    baselineImport: boolean
  }
  documentation: {
    ledgerSafeGuide: boolean
  }
  auth: {
    required: boolean
    allowedDomains: string[]
  }
  landing: {
    requestAccess: boolean
  }
  legacy: {
    booksRoutes: boolean
  }
  [key: string]: unknown
}

export interface RuntimeConfig {
  version: string
  releaseName: string
  releaseDate: string
  profile: string
  defaultRoute: 'home' | 'workbench' | 'books' | 'landing'
  flags: RuntimeFeatureFlags
}

export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  version: '2026.08.0-beta.1',
  releaseName: 'August 2026 Ledger Safe Beta 1',
  releaseDate: '2026-08-09',
  profile: process.env.NODE_ENV === 'production' ? 'professor_demo' : 'development',
  defaultRoute: 'landing',
  flags: {
    workbench: {
      homeDashboard: false,
      reducedMode: process.env.NODE_ENV === 'production',
      reviewTab: process.env.NODE_ENV !== 'production',
      experimentalTabs: process.env.NODE_ENV !== 'production',
      sourceStructure: process.env.NODE_ENV !== 'production'
    },
    ledgerSafe: {
      enabled: true,
      piDock: process.env.NODE_ENV !== 'production',
      logTab: true,
      propagation: process.env.NODE_ENV !== 'production',
      editSuggestions: process.env.NODE_ENV !== 'production',
      experimentalMaps: process.env.NODE_ENV !== 'production',
      newWitnessButton: process.env.NODE_ENV !== 'production',
      oracleExport: true,
      sceneTitles: true,
      rowTitleGeneration: process.env.NODE_ENV !== 'production',
      rowTitleSidecarImport: process.env.NODE_ENV !== 'production',
      structuralRanges: true,
      structuralRangeGeneration: process.env.NODE_ENV !== 'production',
      snapshots: process.env.NODE_ENV !== 'production',
      traditionReader: process.env.NODE_ENV !== 'production',
      characterIndex: process.env.NODE_ENV !== 'production',
      cameraLucida: process.env.NODE_ENV !== 'production',
      postgresTrial: false,
      postgresFullMirror: false,
      postgresPrimary: false,
      postgresPrimaryBootstrap: false,
      postgresPrimaryCutover: false,
      postgresPrimaryFocusedCache: false
    },
    build: {
      adaptiveLargeExecutor: true,
      savedRunImport: true
    },
    sharing: {
      linkSharing: process.env.NODE_ENV !== 'production',
      r2Storage: false,
      baselineImport: process.env.NODE_ENV !== 'production'
    },
    documentation: {
      ledgerSafeGuide: false
    },
    auth: {
      required: process.env.NODE_ENV === 'production',
      allowedDomains: ['fu-berlin.de']
    },
    landing: {
      requestAccess: false
    },
    legacy: {
      booksRoutes: process.env.NODE_ENV !== 'production'
    }
  }
}

interface RuntimeConfigContextValue {
  config: RuntimeConfig
  loaded: boolean
  error: string | null
}

const RuntimeConfigContext = createContext<RuntimeConfigContextValue>({
  config: DEFAULT_RUNTIME_CONFIG,
  loaded: false,
  error: null
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

const mergeRecords = (
  base: Record<string, unknown>,
  override: unknown
): Record<string, unknown> => {
  if (!isRecord(override)) {
    return base
  }
  const next = { ...base } as Record<string, unknown>
  Object.entries(override).forEach(([key, value]) => {
    if (isRecord(value) && isRecord(next[key])) {
      next[key] = mergeRecords(next[key] as Record<string, unknown>, value)
    } else {
      next[key] = value
    }
  })
  return next
}

const mergeFlags = (base: RuntimeFeatureFlags, override: unknown): RuntimeFeatureFlags =>
  mergeRecords(base as unknown as Record<string, unknown>, override) as RuntimeFeatureFlags

const normalizeDefaultRoute = (value: unknown): RuntimeConfig['defaultRoute'] => {
  if (value === 'workbench' || value === 'books' || value === 'home' || value === 'landing') {
    return value
  }
  return DEFAULT_RUNTIME_CONFIG.defaultRoute
}

export const normalizeRuntimeConfig = (payload: unknown): RuntimeConfig => {
  if (!isRecord(payload)) {
    return DEFAULT_RUNTIME_CONFIG
  }
  return {
    version: typeof payload.version === 'string' ? payload.version : DEFAULT_RUNTIME_CONFIG.version,
    releaseName: typeof payload.releaseName === 'string' ? payload.releaseName : DEFAULT_RUNTIME_CONFIG.releaseName,
    releaseDate: typeof payload.releaseDate === 'string' ? payload.releaseDate : DEFAULT_RUNTIME_CONFIG.releaseDate,
    profile: typeof payload.profile === 'string' ? payload.profile : DEFAULT_RUNTIME_CONFIG.profile,
    defaultRoute: normalizeDefaultRoute(payload.defaultRoute),
    flags: mergeFlags(DEFAULT_RUNTIME_CONFIG.flags, payload.flags)
  }
}

export const getRuntimeFeatureFlag = (
  config: RuntimeConfig,
  keyPath: string,
  fallback = false
): boolean => {
  const parts = keyPath.split('.').filter(Boolean)
  let current: unknown = config.flags
  for (const part of parts) {
    if (!isRecord(current) || !(part in current)) {
      return fallback
    }
    current = current[part]
  }
  return typeof current === 'boolean' ? current : fallback
}

export const RuntimeConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<RuntimeConfig>(DEFAULT_RUNTIME_CONFIG)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (typeof fetch !== 'function') {
      setLoaded(true)
      return () => {
        cancelled = true
      }
    }

    fetch('/api/runtime-config', { headers: { Accept: 'application/json' } })
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Runtime config request failed: HTTP ${response.status}`)
        }
        return response.json()
      })
      .then(payload => {
        if (cancelled) {
          return
        }
        setConfig(normalizeRuntimeConfig(payload))
        setLoaded(true)
      })
      .catch(fetchError => {
        if (cancelled) {
          return
        }
        setError(fetchError instanceof Error ? fetchError.message : 'Runtime config request failed')
        setLoaded(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo(() => ({ config, loaded, error }), [config, loaded, error])
  return (
    <RuntimeConfigContext.Provider value={value}>
      {children}
    </RuntimeConfigContext.Provider>
  )
}

export const useRuntimeConfig = (): RuntimeConfigContextValue => useContext(RuntimeConfigContext)

export const useRuntimeFeatureFlag = (keyPath: string, fallback = false): boolean => {
  const { config } = useRuntimeConfig()
  return getRuntimeFeatureFlag(config, keyPath, fallback)
}
